/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Hidden, useScreenClass, Visible } from 'react-grid-system';
import { Icon, Select, Size, TwoWayToggle, Direction, Tooltip, Placement, Button, ButtonType } from '@vaisala/rockhopper-components';
import { VaiColor, VaiIcon } from '@vaisala/rockhopper-design-tokens';

import EnhancedInput from './EnhancedInput';
import InfoButton from './InfoButton';
import { ParameterConsumerProps } from './ParameterControls';
import IconButton from './IconButton';
import { SuperscriptBlock } from './SuperscriptBlock';
import { CardType } from './ParameterCard';

import { ParameterModifier, ParameterValueElement } from '../../modules/calculator/calculatorTypes';
import Analytics from '../../modules/analytics/Analytics';
import { paramNameForRow } from '../../modules/calculator/configParser';
import UnitController from '../../modules/units/UnitController';

export interface ParameterRowProps extends ParameterConsumerProps {
  config: any;
  toleranceEnabled: boolean;
  isActive: boolean;
  isSelectable: boolean;
  showControls: boolean;
  showResults: boolean;
  rowIndex: number;
  cardType: CardType;
  elevationMode: boolean;
  onToggleElevation: (forceElevation?: any) => void;
  setEditCondition: React.Dispatch<React.SetStateAction<'temperature' | 'pressure' | 'elevation' | null>>;
  editCondition: 'temperature' | 'pressure' | 'elevation' | null;
}

const ParameterRow = (props: ParameterRowProps) => {
  const { t } = useTranslation();
  const {
    config,
    toleranceEnabled,
    currentResult,
    onParameterChange,
    isActive,
    isSelectable,
    cardType,
    showControls,
    showResults,
    advancedMode,
    onSelectRow,
    onSelectUnit,
    onOptionsOpen,
    rowIndex,
    elevationMode,
    onToggleElevation,
    setEditCondition,
    editCondition
  } = props;
  const screenClass = useScreenClass();
  const isMobile = () => ['xs', 'sm'].includes(screenClass);

  const valuePrecision = 6;
  const tolerancePrecision = 2;
  const defaultMinMax = 100000000;

  // Elevation if switch toggled. This can be modififed to scale better. JSON needs changes
  const paramName = paramNameForRow(config);

  const [activeCondition, setActiveCondition] = React.useState<'temperature' | 'pressure' | 'elevation' | null>(null);

  const getCurrent = (element: ParameterValueElement) => {
    if (currentResult) {
      const val = currentResult.parameters[paramName][element];
      return UnitController.convertedValue(paramName, val, currentResult, element === 'tolerance');
    } else {
      return '';
    }
  };
  const getCurrentTrimmed = (elem: ParameterValueElement, precision: number) => {
    const val = getCurrent(elem) as number;
    if (isNaN(val)) return '--';
    else if (val === Infinity) return '∞';
    const fval = parseFloat(`${val}`);
    return Math.abs(val) > 1000000 ? Math.round(fval / 1000) + 'k' : fval.toFixed(Math.max(precision - `${Math.round(val)}`.length, 0));
  };

  const onValueChange = (element: ParameterValueElement, inputVal: ParameterModifier) => {
    onParameterChange(paramName, element, UnitController.standardValue(paramName, inputVal, currentResult!, element === 'tolerance'));
  };

  React.useEffect(() => {
    if (isActive && isSelectable) {
      Analytics.log('calculation.select', paramName);
    }
  }, [isActive, isSelectable, paramName]);

  let unitOptions = UnitController.unitsForParameter(paramName).map((o) => UnitController.formatUnit(o));

  const renderUnit = (select: boolean, mobile: boolean = false) => {
    let unit: string;
    // if (mobile && (paramName === 'pressure' || paramName === 'elevation')) {
    //   unit = UnitController.formatUnit(UnitController.unitForParameter(paramName));
    //   unitOptions = UnitController.unitsForParameter('pressure').concat(UnitController.unitsForParameter('elevation')).map((o) => UnitController.formatUnit(o));
    // } else {
    // if (paramName === 'pressure' && elevationMode && select) {
    //   unit = UnitController.formatUnit(UnitController.unitForParameter('elevation'));
    // } else {
      unit = UnitController.formatUnit(UnitController.unitForParameter(paramName));
    // }
    return renderSelect(unit, (_unit, ind) => onSelectUnit(paramName, ind), select ? unitOptions : [unit], Size.S);
  };

  const renderSuperscript = (s: string, className: string) => <SuperscriptBlock text={s} className={className} />;

  const sanitizeMinMax = (val: number) => {
    if (currentResult){
      return parseFloat(`${UnitController.convertedValue(paramName, val, currentResult, false)}`)
    } else {
      return 0;
    }
  }

  const renderModifier = (modifier: any) => (
    <>
      {modifier.type === 'numeric' &&
        renderNumericInput(
          parseFloat(`${getCurrent('value')}`),
          'value',
          sanitizeMinMax(modifier.min ?? -defaultMinMax),
          sanitizeMinMax(modifier.max ?? defaultMinMax),
          Size.S
        )}
      {modifier.type === 'select' && renderSelect(getCurrent('value') as string, (val) => onValueChange('value', val), modifier.options, Size.L)}
    </>
  );

  const renderNumericInput = (value: number, element: ParameterValueElement, min: number, max: number, size: Size) => (
    <div className="controller">
      <EnhancedInput
        width={size}
        value={value}
        setEditCondition={setEditCondition}
        labelKey={config.labelKey}
        hasrelations={config.hasRelations}
        onDelayedChange={(val) => onValueChange(element, val)}
        step={Math.pow(10, -(config.precision ?? 0))}
        min={min}
        max={max}
      />
    </div>
  );

  const renderSelect = (value: string, onChange: (val: string, index: number) => void, options: string[], size: Size) => {
    const hasOptions = options.length > 1;
    const localizedOptions = options.map((o) => t(o));

    const onModalOpen = () =>
      onOptionsOpen(t(config.labelKey), {
        options: localizedOptions,
        selectedIndex: localizedOptions.indexOf(t(value)),
        onSelect: (ind: number) => onChange(options[ind], ind),
      });

    return (
      <>
        <Hidden xs sm>
          <div className="desktop-select">
            {hasOptions && (
              <Select placeholder="Select..." value={value} width={size} onChange={(val, data) => onChange(val, parseInt((data as any).key))}>
                {options.map((option: string, j: number) => (
                  <Select.Option key={j} title={t(option)} value={option}>
                    {renderSuperscript(t(option), '')}
                  </Select.Option>
                ))}
              </Select>
            )}
            {!hasOptions && <div className="select-label">{renderSuperscript(t(value), 'label')}</div>}
          </div>
        </Hidden>
        <Visible xs sm>
          <div className={`mobile-select ${hasOptions ? 'active' : ''}`} onClick={hasOptions ? onModalOpen : undefined}>
            {renderSuperscript(t(value), 'label')}
            {hasOptions && (
              <div className="modal-option-button">
                <IconButton
                  className="info-button tooltip"
                  buttonSize={Size.M}
                  startIcon={<Icon name={VaiIcon.ChevronRight} />}
                  onClick={onModalOpen}
                />
              </div>
            )}
          </div>
        </Visible>
      </>
    );
  };

  const renderMobilePressureSelect = (value: string, onChange: (val: string, index: number) => void, options: string[], size: Size) => {
    const hasOptions = options.length > 1;
    const localizedOptions = options.map((o) => t(o));

    const onModalOpen = () =>
      onOptionsOpen(t(config.labelKey), {
        options: localizedOptions,
        selectedIndex: localizedOptions.indexOf(t(value)),
        onSelect: (ind: number) => options[ind].includes('elevation') ? onToggleElevation(true) : onToggleElevation(false)
      });

    return (
      <>
        <Visible xs sm>
          <div className={`mobile-select mobile-pressure-select ${hasOptions ? 'active' : ''}`} onClick={hasOptions ? onModalOpen : undefined}>
            {renderSuperscript(t(value), 'label')}
            {hasOptions && (
              <div className="modal-option-button">
                <IconButton
                  className="info-button tooltip"
                  buttonSize={Size.M}
                  startIcon={<Icon name={VaiIcon.ChevronRight} />}
                  onClick={onModalOpen}
                />
              </div>
            )}
          </div>
        </Visible>
      </>
    );
  };

  const BlockTitle = () => {
    if (
      (currentResult?.validityStates?.find(s => s.reason === 'tdfCalcInUse') && ['dewFrostPointTemperature'].includes(paramName)) ||
      (currentResult?.validityStates?.find(s => s.reason === 'rhiCalcInUse') && ['relativeHumidityDewFrost', 'waterVaporSaturationPressure'].includes(paramName))
    ) {
      return (
        <>
          <span>
            {t(config.labelKey)}&nbsp;&nbsp;
            <Tooltip
              placement={Placement.BottomStart}
              triggerElement={
                <Button
                  buttonSize={Size.S}
                  buttonType={ButtonType.Link}
                  startIcon={<Icon key="snow-flake" name="snow-flake" color={VaiColor.BlueDark}/>}
                />
                // <Button buttonType={ButtonType.Link} {...props} className={`icon-button icon-button-light`}>
                //   <Icon name={VaiIcon.SnowFlake} size={Size.S} />
                // </Button>
              }
            >
              {t('tooltip.param.frost')}
            </Tooltip>
          </span>
        </>
      );
    } else {
      return <span dangerouslySetInnerHTML={{ __html: t(config.labelKey) }} />;
    }
  };

  const rowClasses = [
    showResults && isActive ? 'active' : '',
    cardType === 'selector' || (cardType === 'humidity' && isMobile() && toleranceEnabled) ? 'double' : '',
    cardType === 'humidity' && config.relatedTo?.includes('temperature') ? 'temperature-hl' : '',
    cardType === 'humidity' && config.relatedTo?.includes('pressure') ? 'pressure-hl' : '',
  ].join(' ');

  const contentClasses = [
    isSelectable ? 'selectable' : '',
    config.modifier.type === 'select' ? 'modifier-select' : 'modifier-numeric',
    isMobile() ? 'mobile' : 'desktop',
    cardType,
    'validity-' + currentResult?.validityStates?.[0]?.severity,
  ].join(' ');

  return (
    <div className={`parameter-row ${rowClasses}`}>
      {isActive && <div className="hit-area" />}
      <div
        className={`content ${contentClasses}`}
        onClick={() => {
          if (isSelectable) onSelectRow(rowIndex, paramName);
        }}
      >
        {paramName === 'pressure' || paramName === 'elevation' ? (
          <div className="block block-title">
            {/* <span dangerouslySetInnerHTML={{__html: 'Elevation'}}/> */}
            <Hidden xs sm>
              <TwoWayToggle
                className="pressure-selector"
                selected={elevationMode ? Direction.Right : Direction.Left}
                onChange={onToggleElevation}
                rightText={t('param.elevation')}
                leftText={t('param.pressure')}
              />
            </Hidden>
            {renderMobilePressureSelect(elevationMode ? t('param.elevation') : t('param.pressure'), (_unit, ind) => onSelectUnit(paramName, ind), ['param.pressure', 'param.elevation'], Size.S)}
          </div>
        ) : (
          <div className="block block-title">
            <BlockTitle />
          </div>
        )}

        <div className="block block-input">
          {showControls && !config.resultOnly && (
            <>
              {renderModifier(config.modifier)}
              {toleranceEnabled && advancedMode && (
                <>
                  <div className="additional">±</div>
                  {renderNumericInput(getCurrent('tolerance') as number, 'tolerance', 0, defaultMinMax, cardType === 'selector' ? Size.XS : Size.XXS)}
                </>
              )}
            </>
          )}
          {showControls && isSelectable && !config.resultOnly && renderUnit(false)}
        </div>

        <div className="block block-unit">
          <div className="results">
            {(showResults || config.resultOnly) && (
              <>
                {`${getCurrentTrimmed('value', valuePrecision)}`}
                {getCurrent('tolerance') > 0 && advancedMode && (
                  <>
                    {isMobile() && <br />}
                    {` ± ${getCurrentTrimmed('tolerance', tolerancePrecision)}`}
                  </>
                )}
              </>
            )}
          </div>
          {renderUnit(UnitController.isManual, isMobile())}
          {showResults && showControls && (
            <div className="info-wrap">{isActive && <InfoButton tooltip={t(`tooltip.${config.labelKey}`)} hPos="left" vPos="top" />}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParameterRow;
